<template>
  <div class="ingredient-search">
    <!-- Main ingredient list -->
    <div
      v-if="selectedIngredients.length > 0"
      class="ingredient-list"
    >
      <div
        v-for="(item, index) in selectedIngredients"
        :key="item.id"
        class="ingredient-item"
      >
        <div class="ingredient-info">
          <span class="ingredient-name">{{ item.name }}</span>
          <div
            v-for="size in mealSizes"
            :key="size.id"
          >
            <div
              class="small text-center mb-1"
              style="padding-right: 30px"
            >
              {{ size.name }}
            </div>
            <div class="ingredient-quantities">
              <div
                v-if="item.countUnit"
                class="quantity-input"
              >
                <input
                  v-model.number="item.counts[size.id]"
                  type="number"
                  min="0"
                  step="0.1"
                  class="form-control form-control-sm"
                  :placeholder="item.countUnit"
                  @change="emitIngredients()"
                >
                <span class="unit">{{ item.countUnit }}</span>
              </div>
              <div
                v-if="item.weightUnit"
                class="quantity-input"
              >
                <input
                  v-model.number="item.weights[size.id]"
                  type="number"
                  min="0"
                  step="0.1"
                  class="form-control form-control-sm"
                  :placeholder="item.weightUnit"
                  @change="emitIngredients()"
                >
                <span class="unit">{{ item.weightUnit }}</span>
              </div>
            </div>
          </div>
        </div>
        <button
          class="btn btn-sm btn-outline-danger"
          type="button"
          @click="removeIngredient(index)"
        >
          <i class="fas fa-times" />
        </button>
      </div>
    </div>

    <!-- Add ingredient button with dropdown -->
    <div class="add-ingredient-section">
      <div
        class="dropdown"
        :class="{ show: showDropdown }"
      >
        <div class="d-flex justify-content-center">
          <button
            ref="addButton"
            class="btn btn-sm btn-outline-primary dropdown-toggle"
            type="button"
            @click="toggleDropdown"
          >
            Add Ingredient
          </button>
        </div>

        <!-- Dropdown menu -->
        <div
          v-show="showDropdown"
          class="dropdown-menu show"
          style="position: absolute; transform: none; top: 100%; left: 0; width: 100%; min-width: 300px;"
        >
          <div class="search-container d-flex justify-content-between">
            <div class="flex-grow-1">
              <input
                v-model="searchQuery"
                type="text"
                class="form-control form-control-sm"
                placeholder="Wyszukaj składniki..."
                @keydown.enter="selectFirstIngredient"
              >
            </div>
            <div
              style="width: 50px; text-align: center;"
              @click="hideDropdown"
            >
              <!-- style="position: absolute; right: -8px; top: -10px; z-index: 100" -->
              <CloseButton
                @click.native.stop="hideDropdown"
              />
            </div>
          </div>

          <div class="ingredients-list">
            <div
              v-for="ingredient in filteredIngredients"
              :key="ingredient.id"
              class="ingredient-option"
              :class="{ 'selected': isSelected(ingredient.id) }"
              @click="selectIngredient(ingredient)"
            >
              <span class="ingredient-name">{{ ingredient.name }}</span>
              <span class="ingredient-units">
                {{ ingredient.countUnit || 'pcs' }} / {{ ingredient.weightUnit || 'g' }}
              </span>
            </div>

            <div
              v-if="filteredIngredients.length === 0"
              class="no-results"
            >
              No ingredients found
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'IngredientSearch',
  props: {
    ingredients: {
      type: Array,
      required: true,
    },
    initIngredients: {
      type: Array,
      required: false,
    },
    mealSizes: {
      type: Array,
      required: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
    dietId: String,
  },
  data() {
    return {
      selectedIngredients: [],
      showDropdown: false,
      searchQuery: '',
    };
  },
  computed: {
    filteredIngredients() {
      if (!this.searchQuery.trim()) {
        return this.ingredients
          .filter((ingredient) => !this.isSelected(ingredient.id));
      }
      const query = this.searchQuery.toLowerCase();
      return this.ingredients
        .filter((ingredient) => !this.isSelected(ingredient.id))
        .filter((ingredient) =>
          ingredient.name.toLowerCase().includes(query));
    },
  },
  watch: {
    selectedIngredients() {
      this.emitIngredients();
    },
  },
  methods: {

    toggleDropdown() {
      if (this.showDropdown) {
        this.hideDropdown();
      } else {
        this.showDropdown = true;
        this.$nextTick(() => {
          this.focusSearch();
        });
      }
    },

    hideDropdown() {
      this.showDropdown = false;
      this.searchQuery = '';
    },

    focusSearch() {
      const searchInput = this.$el.querySelector('.search-container input');
      if (searchInput) {
        searchInput.focus();
      }
    },
    selectIngredient(ingredient) {
      if (this.isSelected(ingredient.id)) {
        return;
      }

      const newIngredient = {
        id: ingredient.id,
        name: ingredient.name,
        countUnit: ingredient.countUnit,
        weightUnit: ingredient.weightUnit,
        counts: {},
        weights: {},
      };

      this.selectedIngredients.push(newIngredient);
      this.emitIngredients();
      this.searchQuery = '';
      this.focusSearch();
    },

    selectFirstIngredient() {
      if (this.filteredIngredients.length > 0) {
        this.selectIngredient(this.filteredIngredients[0]);
      }
    },

    removeIngredient(index) {
      this.selectedIngredients.splice(index, 1);
    },

    isSelected(ingredientId) {
      return this.selectedIngredients.some((item) => item.id === ingredientId);
    },

    emitIngredients() {
      const flat = this.selectedIngredients.flatMap((e) =>
        this.mealSizes.map((mealSize) => ({
          dietId: this.dietId,
          mealSizeId: mealSize.id,
          id: e.id,
          count: e.counts[mealSize.id],
          weight: e.weights[mealSize.id],
        })));

      this.$emit('ingredients', flat);
    },

  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  mounted() {
    if (this.initIngredients) {
      this.initIngredients.forEach((x) => {
        let m = this.selectedIngredients.find((i) => i.id === x.id);

        if (!m) {
          const ingredient = this.ingredients.find((i) => i.id === x.id);
          m = {
            id: x.id,
            name: ingredient.name,
            countUnit: ingredient.countUnit,
            weightUnit: ingredient.weightUnit,
            counts: {},
            weights: {},
          };

          this.selectedIngredients.push(m);
        }

        if (m) {
          m.counts[x.mealSizeId] = x.count;
          m.weights[x.mealSizeId] = x.weight;
        }
      },
      {});

      this.emitIngredients();
    }
  },

};
</script>

<style lang="scss" scoped>
.ingredient-search {
  position: relative;
}

.ingredient-list {
  margin-bottom: 1rem;
}

.ingredient-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  background-color: rgba(#f8f9fa, 0.5);

  &:last-child {
    margin-bottom: 0;
  }
}

.ingredient-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.ingredient-name {
  font-weight: 500;
  min-width: 120px;
}

.ingredient-quantities {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
}

.quantity-input {
  display: flex;
  align-items: center;
  gap: 0.25rem;

  input {
    width: 80px;
  }

  .unit {
    font-size: 0.875rem;
    color: #6c757d;
    min-width: 30px;
  }
}

.dropdown {
  position: relative;
}

.dropdown-menu {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  z-index: 1050;
  padding: 0;
  margin-top: 0.125rem;
}

.search-container {
  padding: 0.5rem;

  border-bottom: 1px solid #dee2e6;
}

.ingredients-list {
  max-height: 300px;
  overflow-y: auto;
}

.ingredient-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: #f8f9fa;
  }

  &.selected {
    background-color: #e3f2fd;
    color: #1976d2;
  }

  &:last-child {
    border-bottom: none;
  }
}

.ingredient-option .ingredient-name {
  font-weight: 500;
}

.ingredient-option .ingredient-units {
  font-size: 0.875rem;
  color: #6c757d;
}

.no-results {
  padding: 0.75rem;
  text-align: center;
  color: #6c757d;
  font-style: italic;
}
</style>
